import React, { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { OcPortalLayout } from "@/components/layout/OcPortalLayout";
import { OcSkeletonTable } from "@/components/shared/OcSkeletonTable";
import { OcLoginPage } from "@/pages/OcLoginPage";
import { OcAcceptInvitePage } from "@/pages/OcAcceptInvitePage";
import { ocFetch, ocGetSessionToken, ocClearSessionToken, ocRecordActivity, ocIsSessionExpired, OcApiError } from "@/lib/oc-api";
import { useOcSessionStore } from "@/store/oc-session-store";
import type { OcTenant, OcPermissionKey } from "@oc/types";

// Lazy-loaded page components
const OcSetupPage = React.lazy(() =>
  import("@/pages/OcSetupPage").then((m) => ({ default: m.OcSetupPage }))
);
const OcDashboardPage = React.lazy(() =>
  import("@/pages/OcDashboardPage").then((m) => ({ default: m.OcDashboardPage }))
);
const OcStudentsPage = React.lazy(() =>
  import("@/pages/OcStudentsPage").then((m) => ({ default: m.OcStudentsPage }))
);
const OcProvisioningRulesPage = React.lazy(() =>
  import("@/pages/OcProvisioningRulesPage").then((m) => ({ default: m.OcProvisioningRulesPage }))
);
const OcScheduledDeletionsPage = React.lazy(() =>
  import("@/pages/OcScheduledDeletionsPage").then((m) => ({ default: m.OcScheduledDeletionsPage }))
);
const OcSlateIntegrationPage = React.lazy(() =>
  import("@/pages/OcSlateIntegrationPage").then((m) => ({ default: m.OcSlateIntegrationPage }))
);
const OcAdConnectionPage = React.lazy(() =>
  import("@/pages/OcAdConnectionPage").then((m) => ({ default: m.OcAdConnectionPage }))
);
const OcAgentsPage = React.lazy(() =>
  import("@/pages/OcAgentsPage").then((m) => ({ default: m.OcAgentsPage }))
);
const OcAuditLogPage = React.lazy(() =>
  import("@/pages/OcAuditLogPage").then((m) => ({ default: m.OcAuditLogPage }))
);
const OcPasswordResetPage = React.lazy(() =>
  import("@/pages/OcPasswordResetPage").then((m) => ({ default: m.OcPasswordResetPage }))
);
const OcReportsPage = React.lazy(() =>
  import("@/pages/OcReportsPage").then((m) => ({ default: m.OcReportsPage }))
);
const OcUsersRolesPage = React.lazy(() =>
  import("@/pages/OcUsersRolesPage").then((m) => ({ default: m.OcUsersRolesPage }))
);
const OcSsoSettingsPage = React.lazy(() =>
  import("@/pages/OcSsoSettingsPage").then((m) => ({ default: m.OcSsoSettingsPage }))
);
const OcStudentDetailPage = React.lazy(() =>
  import("@/pages/OcStudentDetailPage").then((m) => ({ default: m.OcStudentDetailPage }))
);
const OcWorkflowsPage = React.lazy(() =>
  import("@/pages/OcWorkflowsPage").then((m) => ({ default: m.OcWorkflowsPage }))
);
const OcConnectionsPage = React.lazy(() =>
  import("@/pages/OcConnectionsPage").then((m) => ({ default: m.OcConnectionsPage }))
);
const OcCredentialsPage = React.lazy(() =>
  import("@/pages/OcCredentialsPage").then((m) => ({ default: m.OcCredentialsPage }))
);
const OcDeveloperPage = React.lazy(() =>
  import("@/pages/OcDeveloperPage").then((m) => ({ default: m.OcDeveloperPage }))
);
const OcUsagePage = React.lazy(() =>
  import("@/pages/OcUsagePage").then((m) => ({ default: m.OcUsagePage }))
);
const OcNotFoundPage = React.lazy(() =>
  import("@/pages/OcNotFoundPage").then((m) => ({ default: m.OcNotFoundPage }))
);
const OcSsoCompletePage = React.lazy(() =>
  import("@/pages/OcSsoCompletePage").then((m) => ({ default: m.OcSsoCompletePage }))
);
const OcSupportPage = React.lazy(() =>
  import("@/pages/OcSupportPage").then((m) => ({ default: m.OcSupportPage }))
);
const OcEmailTemplatesPage = React.lazy(() =>
  import("@/pages/OcEmailTemplatesPage").then((m) => ({ default: m.OcEmailTemplatesPage }))
);
const OcSearchSettingsPage = React.lazy(() =>
  import("@/pages/OcSearchSettingsPage").then((m) => ({ default: m.OcSearchSettingsPage }))
);
const OcActionsConfigurationPage = React.lazy(() =>
  import("@/pages/OcActionsConfigurationPage").then((m) => ({ default: m.OcActionsConfigurationPage }))
);
const OcDirectoryUserDetailPage = React.lazy(() =>
  import("@/pages/OcDirectoryUserDetailPage").then((m) => ({ default: m.OcDirectoryUserDetailPage }))
);
const OcProvisioningSettingsPage = React.lazy(() =>
  import("@/pages/OcProvisioningSettingsPage").then((m) => ({ default: m.OcProvisioningSettingsPage }))
);
const OcAlertsPage = React.lazy(() =>
  import("@/pages/OcAlertsPage").then((m) => ({ default: m.OcAlertsPage }))
);
const OcSelfServicePage = React.lazy(() => import("@/pages/OcSelfServicePage"));
const OcSelfServiceResetLogPage = React.lazy(() => import("@/pages/OcSelfServiceResetLogPage"));

function OcPageFallback() {
  return (
    <div className="p-6">
      <OcSkeletonTable rows={5} />
    </div>
  );
}

// ─── Auth guard ───────────────────────────────────────────────────────────────

type OcAuthState = "loading" | "authenticated" | "unauthenticated";

interface OcMeResponse {
  user: { name: string; email: string; role: string; permissions: OcPermissionKey[] };
  tenant: OcTenant | null;
  onboardingComplete: boolean;
}

function OcAuthGuard({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<OcAuthState>("loading");
  const navigate = useNavigate();
  const location = useLocation();
  const { setTenant, setUser, setPermissions, setOnboardingComplete, clearSession } = useOcSessionStore();

  useEffect(() => {
    const token = ocGetSessionToken();
    if (!token) {
      setAuthState("unauthenticated");
      return;
    }
    ocFetch<OcMeResponse>("/tenant/auth/me")
      .then((me) => {
        if (me.tenant) setTenant(me.tenant);
        setUser(me.user.name, me.user.email);
        setPermissions(me.user.permissions);
        setOnboardingComplete(me.onboardingComplete);
        setAuthState("authenticated");
      })
      .catch((err) => {
        if (err instanceof OcApiError && err.status === 401) {
          ocClearSessionToken();
        }
        setAuthState("unauthenticated");
      });
  }, []);

  // Inactivity + max-session watcher (active only when authenticated)
  useEffect(() => {
    if (authState !== "authenticated") return;

    const EVENTS = ["click", "keydown", "mousemove", "scroll", "touchstart"] as const;
    EVENTS.forEach((ev) => window.addEventListener(ev, ocRecordActivity, { passive: true }));

    const interval = setInterval(() => {
      if (ocIsSessionExpired()) {
        ocClearSessionToken();
        clearSession();
        setAuthState("unauthenticated");
      }
    }, 60_000);

    return () => {
      EVENTS.forEach((ev) => window.removeEventListener(ev, ocRecordActivity));
      clearInterval(interval);
    };
  }, [authState]);

  if (authState === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-slate-500 text-xs font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-oc-teal-500 animate-pulse" />
          Loading…
        </div>
      </div>
    );
  }

  if (authState === "unauthenticated") {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ─── App ──────────────────────────────────────────────────────────────────────

export function OcApp() {
  return (
    <Routes>
      <Route path="/login" element={<OcLoginPage />} />
      <Route path="/accept-invite" element={<OcAcceptInvitePage />} />
      <Route
        path="/auth/sso/complete"
        element={
          <Suspense fallback={<OcPageFallback />}>
            <OcSsoCompletePage />
          </Suspense>
        }
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      <Route
        element={
          <OcAuthGuard>
            <OcPortalLayout />
          </OcAuthGuard>
        }
      >
        <Route
          path="/setup"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSetupPage />
            </Suspense>
          }
        />
        <Route
          path="/dashboard"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcDashboardPage />
            </Suspense>
          }
        />
        <Route path="/students" element={<Navigate to="/directory-search" replace />} />
        <Route path="/students/:id" element={<Navigate to="/directory-search" replace />} />
        <Route
          path="/directory-search"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcStudentsPage />
            </Suspense>
          }
        />
        <Route
          path="/directory-search/:id"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcDirectoryUserDetailPage />
            </Suspense>
          }
        />
        <Route
          path="/provisioning-rules"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcProvisioningRulesPage />
            </Suspense>
          }
        />
        <Route
          path="/workflows"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcWorkflowsPage />
            </Suspense>
          }
        />
        <Route
          path="/connections"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcConnectionsPage />
            </Suspense>
          }
        />
        <Route path="/search-settings" element={<Navigate to="/record-management" replace />} />
        <Route
          path="/record-management"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSearchSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="/actions-configuration"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcActionsConfigurationPage />
            </Suspense>
          }
        />
        <Route path="/provisioning-settings" element={<Navigate to="/provision-rules" replace />} />
        <Route
          path="/provision-rules"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcProvisioningSettingsPage />
            </Suspense>
          }
        />
        <Route
          path="/credentials"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcCredentialsPage />
            </Suspense>
          }
        />
        <Route
          path="/developer"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcDeveloperPage />
            </Suspense>
          }
        />
        <Route
          path="/email-templates"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcEmailTemplatesPage />
            </Suspense>
          }
        />
        <Route
          path="/scheduled-deletions"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcScheduledDeletionsPage />
            </Suspense>
          }
        />
        <Route
          path="/slate-integration"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSlateIntegrationPage />
            </Suspense>
          }
        />
        <Route
          path="/ad-connection"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcAdConnectionPage />
            </Suspense>
          }
        />
        <Route
          path="/agents"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcAgentsPage />
            </Suspense>
          }
        />
        <Route
          path="/audit-log"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcAuditLogPage />
            </Suspense>
          }
        />
        <Route
          path="/password-reset"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcPasswordResetPage />
            </Suspense>
          }
        />

        {/* Reports with sub-routes */}
        <Route
          path="/reports"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcReportsPage />
            </Suspense>
          }
        >
          <Route index element={<Navigate to="/reports/disabled-users" replace />} />
          <Route path="disabled-users" element={null} />
          <Route path="new-ad-users" element={null} />
          <Route path="new-azure-users" element={null} />
          <Route path="password-resets" element={null} />
          <Route path="failed-provisioning" element={null} />
          {/* Slate Accepts dropped — was a placeholder with no data source. */}
          <Route path="slate-accepts" element={<Navigate to="/reports/all-students" replace />} />
          <Route path="all-students" element={null} />
          <Route path="saved" element={null} />
        </Route>

        <Route path="/usage" element={<Navigate to="/plan-usage" replace />} />
        <Route
          path="/plan-usage"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcUsagePage />
            </Suspense>
          }
        />

        <Route
          path="/support"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSupportPage />
            </Suspense>
          }
        />

        <Route
          path="/alerts"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcAlertsPage />
            </Suspense>
          }
        />

        <Route
          path="/users-roles"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcUsersRolesPage />
            </Suspense>
          }
        />

        <Route
          path="/sso-settings"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSsoSettingsPage />
            </Suspense>
          }
        />

        <Route
          path="/self-service"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSelfServicePage />
            </Suspense>
          }
        />

        <Route
          path="/password-reset-log"
          element={
            <Suspense fallback={<OcPageFallback />}>
              <OcSelfServiceResetLogPage />
            </Suspense>
          }
        />
      </Route>

      {/* 404 — outside layout so it renders full-page */}
      <Route
        path="*"
        element={
          <Suspense fallback={<OcPageFallback />}>
            <OcNotFoundPage />
          </Suspense>
        }
      />
    </Routes>
  );
}
